import { useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, HighlightCard, Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Zap, AlertTriangle, Plug, ArrowRight } from 'lucide-react';
import { UsedInStudiosPanel } from '../../components';
import { useIntegrationsPermissions } from '../../hooks';
import AboutSections from './parts/AboutSections.jsx';
import DetailsSidebar from './parts/DetailsSidebar.jsx';
import InstanceRow from './parts/InstanceRow.jsx';
import SectionCard from './parts/SectionCard.jsx';

const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-content-faint';

/**
 * Provider Detail — Overview tab (CT-4179, default). Two connection states:
 *
 * - Connected: an "Instances" panel (HighlightCard metrics + the instance list with
 *   a "Manage all" link), the Used-in-Studios panel, and the About sections
 *   including "Required scopes".
 * - Not connected: no tiles/instances/studios — the About sections and a
 *   "Before you connect" block (scopes + Connect CTA), matching the prototype.
 *
 * The details sidebar renders in both states.
 *
 * Deviation from CT-4186's written AC ("Used-in-Studios all 'Not enabled'" for
 * not-connected providers): the actual prototype has no Used-in-Studios section
 * at all in the not-connected state — the header's Connect CTA + badge already
 * communicate the connection state, so repeating an all-disabled studios panel
 * would be redundant. Prototype fidelity wins over the AC text here; flagged on
 * CT-4186/CT-4179 rather than silently diverging.
 */
const OverviewTab = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { provider, onConnect } = useOutletContext();
  const { canManageIntegrations } = useIntegrationsPermissions();

  const capabilitiesActive = provider.capabilities.filter((c) => c.active_instances > 0).length;
  const anyStudioEnabled = Object.values(provider.studios).some((s) => s?.enabled);

  // "instances/:id" is a sibling of the "overview" leaf route, not a child of
  // it — go up a level first, or relative resolution nests it under
  // /overview/instances/… and dead-ends on the tab bar's catch-all redirect.
  // Stabilized so InstanceRow's memo isn't defeated by a fresh closure per render.
  const handleOpenInstance = useCallback(
    (instanceId) => navigate(`../instances/${instanceId}`),
    [navigate],
  );
  const openLabel = t('integrations.provider.overview.open');

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="flex flex-1 flex-col gap-4">
        {provider.isConnected ? (
          <>
            <Card>
              <Card.Body>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-content">
                    {t('integrations.provider.overview.instances')}
                  </h3>
                  {/* "instances" is an admin-only tab — gate this the same way
                      the per-row "Open" CTA below is gated, so the link doesn't
                      dead-end for end users once RBAC (CT-3900) makes
                      canManageIntegrations actually vary. */}
                  {canManageIntegrations && (
                    <button
                      type="button"
                      onClick={() => navigate('../instances')}
                      className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                    >
                      {t('integrations.provider.overview.manageAll')}
                      <ArrowRight size={13} aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <HighlightCard
                    variant="green"
                    label={t('integrations.provider.overview.connected')}
                    value={provider.breakdown.working_count}
                    icon={Plug}
                    showUnit={false}
                    showFeedback={false}
                  />
                  <HighlightCard
                    variant="orange"
                    label={t('integrations.provider.overview.needAttention')}
                    value={provider.breakdown.needs_attention_count}
                    icon={AlertTriangle}
                    showUnit={false}
                    showFeedback={false}
                  />
                  <HighlightCard
                    variant="neutral"
                    label={t('integrations.provider.overview.capabilitiesActive')}
                    value={capabilitiesActive}
                    icon={Zap}
                    showUnit={false}
                    showFeedback={false}
                  />
                </div>

                {provider.instances.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    {provider.instances.map((inst) => (
                      <InstanceRow
                        key={inst.id}
                        instance={inst}
                        canManageIntegrations={canManageIntegrations}
                        onOpen={handleOpenInstance}
                        openLabel={openLabel}
                      />
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className={SECTION_LABEL}>
                    {t('integrations.provider.overview.usedInStudios')}
                  </h3>
                  {anyStudioEnabled && (
                    <span className="text-xs text-content-faint">
                      {t('integrations.provider.overview.manageStudiosHint')}
                    </span>
                  )}
                </div>
                <UsedInStudiosPanel studios={provider.studios} />
              </Card.Body>
            </Card>

            <AboutSections t={t} provider={provider} showScopes />
          </>
        ) : (
          <>
            <AboutSections t={t} provider={provider} />

            <SectionCard title={t('integrations.provider.overview.beforeYouConnect')}>
              <p className="mb-3 text-sm text-content-muted">
                {t('integrations.provider.overview.beforeYouConnectHint')}
              </p>
              {provider.scopes.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {provider.scopes.map((scope) => (
                    <Badge key={scope} variant="neutral" size="small" radius="default">
                      {scope}
                    </Badge>
                  ))}
                </div>
              )}
              <Button variant="classicPrimary" size="sm" onClick={onConnect}>
                {t('integrations.provider.connect', { name: provider.name })}
              </Button>
            </SectionCard>
          </>
        )}
      </div>

      <DetailsSidebar t={t} details={provider.details} />
    </div>
  );
};

export default OverviewTab;
